// @ts-nocheck
import { Menu, message } from 'antd';
import React, { useContext, useState } from 'react';
import config from '$config';
import InvitationCancellationModal from '../InvitationCancellationModal';
import { BOOKING_STATUS } from '../../../constants';
import LoadingModal from '$app/shared/components/LoadingModal';
import sendConfirmRescheduleRequest from './api';
import { patchBooking as patchBookingRequest, patchJobInvitation as patchJobInvitationRequest } from '../../api';
import { getSelectedJobInvite } from '../../../utils';
import BookingPageContext from '../../../context';
import {
  invitesHaveCustomerConfirmedProposedChanges,
  invitesHaveCaseManagerReviewedProposedChanges,
  invitationHasGarageProposedDates,
} from './logic';

const getErrorMessage = (error, fallback) => error?.response?.data?.message || error?.message || fallback;

const getViewWorkDetailsUrl = (bookingId, invitationId, inviteIsReadOnly) => {
  let url = `bookings/${bookingId}?jobInvitationId=${invitationId}`;

  if (inviteIsReadOnly) url += '&readonly=true';

  return url;
};

const inviteCanBeCancelled = (booking, invitation) =>
  (booking.masterStatusId < BOOKING_STATUS.WORK_COMPLETE &&
    ['pending', 'review', 'accepted'].includes(invitation.status)) ||
  invitation.status === 'draft';
/**
 * You cannot edit an invitaion once the work has been completed. E.g the booking is past AT_GARAGE
 */

const { Item } = Menu;

const InviteMenu = ({ invitation, booking, garageInvites }) => {
  const [isLoadingModalVisible, setLoadingModalVisible] = useState(false);
  const { reloadBooking } = useContext(BookingPageContext);

  const garyAutoLoginLink = invitation.garage.autoLoginLink;
  const inviteIsReadOnly = ['cancelled', 'declined', 'expired'].includes(invitation.status);

  const showGaryBtn = garyAutoLoginLink;

  const { status, bookingId, invitationId } = invitation;

  const selectedInviteId = getSelectedJobInvite({
    garageInvites,
    masterStatusId: booking.masterStatusId,
  })?.invitationId;

  const viewWorkDetailsUrl = getViewWorkDetailsUrl(bookingId, invitationId, inviteIsReadOnly);
  const alreadySelectedInvite = selectedInviteId === invitationId;
  const showViewWorkDetailsLink = !alreadySelectedInvite;

  const currentInvite = invitation;
  const currentInviteNotInReviewStatus = currentInvite.status !== 'review';

  const shouldDisplayAcceptInviteButton = (garageInvites, invitation) => {
    if (currentInviteNotInReviewStatus) return false;

    if (
      !invitationHasGarageProposedDates(invitation) && // current invite doesn't have garage proposed dates
      invitesHaveCaseManagerReviewedProposedChanges(garageInvites) // booking is in CM reschedule review
    )
      return false;

    if (
      // eslint-disable-next-line max-len
      invitesHaveCustomerConfirmedProposedChanges(garageInvites) && // booking is in customer confirmed reschedule flow
      invitationHasGarageProposedDates(invitation) // current invite has garage proposed dates
      /**
       * Booking has already entered the reschedule flow with the customer confirming a specific date.
       * The CM should not be able to accept a garage proposed date (that was submitted after the customer
       * confirmed) - while the invites created via the reschedule flow are still in 'review' status.
       */
    )
      return false;

    return true;
  };

  const displayAcceptReschedule = shouldDisplayAcceptInviteButton(garageInvites, invitation);

  const acceptNewTimeV1 = async () => {
    const changes = {
      allocationStatus: 'allocated',
    };
    invitation?.proposedChanges?.forEach((pc) => {
      if (pc.createdBy === 'garage' && pc.type === 'collection-time') {
        changes.collectionDateTimeLocal = pc.value;
      }
      if (pc.createdBy === 'garage' && pc.type === 'delivery-time') {
        changes.deliveryDateTimeLocal = pc.value;
      }
    });

    try {
      await patchJobInvitationRequest(invitationId, { status: 'accepted' });
      await patchBookingRequest(bookingId, changes);
      reloadBooking();
    } catch (error) {
      message.error(getErrorMessage(error, 'Unable to accept invite'));
    }
  };

  const acceptNewTimeV2 = async () => {
    setLoadingModalVisible(true);

    try {
      let collectionDateTimeLocal;
      invitation?.proposedChanges?.forEach(({ createdBy, type, status, value }) => {
        if (createdBy === 'garage' && type === 'collection-time' && status === 'proposed') {
          collectionDateTimeLocal = value;
        }
      });
      await sendConfirmRescheduleRequest(bookingId, collectionDateTimeLocal, invitationId);
      reloadBooking();
    } catch (error) {
      message.error(getErrorMessage(error, 'Unable to accept invite'));
    } finally {
      setLoadingModalVisible(false);
    }
  };
  const acceptNewTime = config.featureFlags?.AUTO_RESCHEDULE_FLOW_ENABLED ? acceptNewTimeV2 : acceptNewTimeV1;

  return (
    <Menu>
      <LoadingModal
        visible={isLoadingModalVisible}
        title="Accepting invite"
        subtitle="Please wait, this could take up 30 seconds."
      />
      {displayAcceptReschedule && (
        <Item onClick={() => acceptNewTime()}>
          {invitesHaveCustomerConfirmedProposedChanges(garageInvites)
            ? 'Accept invite'
            : 'Accept invite and reschedule booking'}
        </Item>
      )}
      {showViewWorkDetailsLink && (
        <Item>
          <a href={viewWorkDetailsUrl}>View work details</a>
        </Item>
      )}
      {showGaryBtn && (
        <Item>
          <a href={garyAutoLoginLink} target="_blank" rel="noreferrer">
            Open Gary
          </a>
        </Item>
      )}
      {inviteCanBeCancelled(booking, invitation) && (
        <InvitationCancellationModal
          key={invitationId}
          invitationId={invitationId}
          invitationStatus={status}
          garageName={invitation.garage.businessName}
          bookingId={bookingId}
          garageInvites={garageInvites}
        />
      )}
    </Menu>
  );
};

export default InviteMenu;
